import { TFunction } from 'react-i18next';
import {
  KizenNodeType,
  BranchStepTypes,
  LoopStepTypes,
} from 'pages/AutomationEngine/types/automation';
import { ValidIcons } from '@kizen/kds/Icon';
import { SemanticColorName } from '@kizen/kds/Colors';

export const STEP_NODE_TYPES = {
  TRIGGER: 'trigger',
  CONDITION: 'condition',
  GOAL: 'goal',
  ACTION: 'action',
  DELAY: 'delay',
  ERROR: 'error',
  INITIALIZE_VARIABLE: 'initialize_variable',
  AGENT: 'agent',
  LOOP: 'loop',
  BRANCH: 'branch',
} as const;

export type StepNodeType = ValueOf<typeof STEP_NODE_TYPES>;
type StepNodeConfig = {
  stepNodeType: StepNodeType;
  label: (t: TFunction) => string;
  color: SemanticColorName;
  bgColor: SemanticColorName;
  icon: ValidIcons;
};

export const STEP_NODE_CONFIG: Record<StepNodeType, StepNodeConfig> = {
  [STEP_NODE_TYPES.TRIGGER]: {
    stepNodeType: STEP_NODE_TYPES.TRIGGER,
    label: (t) => t('Trigger'),
    color: 'agentic-workflow-trigger-primary',
    bgColor: 'agentic-workflow-trigger-secondary',
    icon: 'badge-agentic-workflow-trigger',
  },
  [STEP_NODE_TYPES.CONDITION]: {
    stepNodeType: STEP_NODE_TYPES.CONDITION,
    label: (t) => t('Condition'),
    color: 'agentic-workflow-condition-primary',
    bgColor: 'agentic-workflow-condition-secondary',
    icon: 'badge-agentic-workflow-condition',
  },
  [STEP_NODE_TYPES.GOAL]: {
    stepNodeType: STEP_NODE_TYPES.GOAL,
    label: (t) => t('Goal'),
    color: 'agentic-workflow-trigger-primary',
    bgColor: 'agentic-workflow-trigger-secondary',
    icon: 'badge-agentic-workflow-goal',
  },
  [STEP_NODE_TYPES.DELAY]: {
    stepNodeType: STEP_NODE_TYPES.DELAY,
    label: (t) => t('Delay'),
    color: 'agentic-workflow-delay-primary',
    bgColor: 'agentic-workflow-delay-secondary',
    icon: 'badge-agentic-workflow-delay',
  },
  [STEP_NODE_TYPES.ACTION]: {
    stepNodeType: STEP_NODE_TYPES.ACTION,
    label: (t) => t('Action'),
    color: 'agentic-workflow-action-primary',
    bgColor: 'agentic-workflow-action-secondary',
    icon: 'badge-agentic-workflow-action',
  },
  [STEP_NODE_TYPES.ERROR]: {
    stepNodeType: STEP_NODE_TYPES.ERROR,
    label: (t) => t('Error'),
    color: 'agentic-workflow-error-primary',
    bgColor: 'agentic-workflow-error-secondary',
    icon: 'badge-agentic-workflow-error',
  },
  [STEP_NODE_TYPES.INITIALIZE_VARIABLE]: {
    stepNodeType: STEP_NODE_TYPES.INITIALIZE_VARIABLE,
    label: (t) => t('Initialize Variable'),
    color: 'agentic-workflow-variable-primary',
    bgColor: 'agentic-workflow-variable-secondary',
    icon: 'badge-agentic-workflow-variable',
  },
  [STEP_NODE_TYPES.AGENT]: {
    stepNodeType: STEP_NODE_TYPES.AGENT,
    label: (t) => t('AI Agent'),
    color: 'agentic-workflow-agent-primary',
    bgColor: 'agentic-workflow-agent-secondary',
    icon: 'badge-agentic-workflow-agent',
  },
  [STEP_NODE_TYPES.LOOP]: {
    stepNodeType: STEP_NODE_TYPES.LOOP,
    label: (t) => t('Loop'),
    color: 'agentic-workflow-loop-primary',
    bgColor: 'agentic-workflow-loop-secondary',
    icon: 'badge-agentic-workflow-loop',
  },
  [STEP_NODE_TYPES.BRANCH]: {
    stepNodeType: STEP_NODE_TYPES.BRANCH,
    label: (t) => t('Branch'),
    color: 'agentic-workflow-loop-primary',
    bgColor: 'agentic-workflow-loop-secondary',
    icon: 'badge-agentic-workflow-branch',
  },
};

export type StepDisplayConfig = StepNodeConfig & {
  stepNodeType: StepNodeType;
  type: KizenNodeType;
  color: string;
  movable: boolean;
  creatable: boolean;
  deletable: boolean;
  duplicatable?: boolean;
  copyable?: boolean;
  actionType?: boolean;
  triggerType?: boolean;
  goalType?: boolean;
  loopType?: boolean;
  prefix: 'action' | 'trigger' | 'step' | 'initialize_variable';
  mergeable?: boolean;
  CardComponent?: string;
  loopDepth?: null | number;
};

export const actionDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.ACTION],
  movable: true,
  creatable: true,
  deletable: true,
  duplicatable: true,
  copyable: true,
  actionType: true,
  prefix: 'action',
};

export const agentDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.AGENT],
  movable: true,
  creatable: true,
  deletable: true,
  duplicatable: true,
  copyable: true,
  actionType: true,
  prefix: 'action',
};

export const triggerDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.TRIGGER],
  deletable: true,
  creatable: true,
  movable: false,
  triggerType: true,
  prefix: 'trigger',
};

const goalDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.GOAL],
  movable: true,
  creatable: true,
  deletable: true,
  duplicatable: true,
  copyable: true,
  goalType: true,
  prefix: 'step',
  mergeable: true,
};

export const mergeDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.BRANCH],
  movable: true,
  creatable: false,
  deletable: true,
  duplicatable: true,
  copyable: true,
  mergeable: true,
  prefix: 'step',
};

export const loopDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.LOOP],
  loopType: true,
  movable: true,
  creatable: false,
  deletable: true,
  duplicatable: false,
  copyable: true,
  mergeable: false,
  prefix: 'step',
};

export const variableDefinitionDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.INITIALIZE_VARIABLE],
  movable: true,
  deletable: true,
  creatable: true,
  triggerType: false,
  prefix: 'initialize_variable',
};

const conditionDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.CONDITION],
  movable: true,
  creatable: true,
  deletable: true,
  duplicatable: true,
  copyable: true,
  prefix: 'step',
  mergeable: true,
};

const delayDisplay: Omit<StepDisplayConfig, 'type'> = {
  ...STEP_NODE_CONFIG[STEP_NODE_TYPES.DELAY],
  movable: true,
  creatable: true,
  deletable: true,
  duplicatable: true,
  copyable: true,
  prefix: 'step',
  mergeable: false,
};

export const AUTOMATION_STEPS: Record<KizenNodeType, StepDisplayConfig> = {
  // Action types
  http_request: {
    type: 'http_request',
    ...actionDisplay,
    CardComponent: 'HttpRequestCard',
  },
  change_field_value: {
    type: 'change_field_value',
    ...actionDisplay,
    CardComponent: 'ChangeFieldValueCard',
  },
  start_automation: {
    type: 'start_automation',
    ...actionDisplay,
    CardComponent: 'StartAutomationCard',
  },
  modify_automation: {
    type: 'modify_automation',
    ...actionDisplay,
    CardComponent: 'ModifyAutomationCard',
  },
  create_related_entity: {
    type: 'create_related_entity',
    ...actionDisplay,
    CardComponent: 'CreateRelatedEntityCard',
  },
  archive_record: {
    type: 'archive_record',
    ...actionDisplay,
    CardComponent: 'ArchiveRecordCard',
  },
  search_records: {
    type: 'search_records',
    ...actionDisplay,
    CardComponent: 'SearchRecordsCard',
  },
  modify_related_entities: {
    type: 'modify_related_entities',
    ...actionDisplay,
    CardComponent: 'ModifyRelatedEntitiesCard',
  },
  send_related_contact_email: {
    type: 'send_related_contact_email',
    ...actionDisplay,
    CardComponent: 'SendRelatedContactEmailCard',
  },
  change_tags: {
    type: 'change_tags',
    ...actionDisplay,
    CardComponent: 'ChangeTagsCard',
  },
  send_email: {
    type: 'send_email',
    ...actionDisplay,
    CardComponent: 'SendEmailCard',
  },
  send_text: {
    type: 'send_text',
    ...actionDisplay,
    CardComponent: 'SendTextCard',
  },
  send_related_contact_text: {
    type: 'send_related_contact_text',
    ...actionDisplay,
    CardComponent: 'SendRelatedContactTextCard',
  },
  notify_member_via_text: {
    type: 'notify_member_via_text',
    ...actionDisplay,
    CardComponent: 'NotifyMemberViaTextCard',
  },
  notify_member_via_email: {
    type: 'notify_member_via_email',
    ...actionDisplay,
    CardComponent: 'NotifyMemberViaEmailCard',
  },
  schedule_activity: {
    type: 'schedule_activity',
    ...actionDisplay,
    CardComponent: 'ScheduleActivityCard',
  },
  delete_scheduled_activity: {
    type: 'delete_scheduled_activity',
    ...actionDisplay,
    CardComponent: 'DeleteScheduledActivityCard',
  },
  assign_team_member: {
    type: 'assign_team_member',
    ...actionDisplay,
    CardComponent: 'AssignTeamMemberCard',
  },
  math_operator: {
    type: 'math_operator',
    ...actionDisplay,
    CardComponent: 'MathOperatorCard',
  },
  go_to_automation_step: {
    type: 'go_to_automation_step',
    ...actionDisplay,
    CardComponent: 'GoToAutomationStepCard',
  },
  update_variable: {
    type: 'update_variable',
    ...actionDisplay,
    CardComponent: 'UpdateVariableCard',
  },
  code_step: {
    type: 'code_step',
    ...actionDisplay,
    CardComponent: 'CodeStepCard',
  },
  stop_execution: {
    type: 'stop_execution',
    ...actionDisplay,
    CardComponent: 'StopExecutionCard',
  },
  plugin_code_step: {
    type: 'plugin_code_step',
    ...actionDisplay,
    CardComponent: 'PluginCodeStepCard',
  },
  // Agent types
  call_llm: {
    type: 'call_llm',
    ...agentDisplay,
    CardComponent: 'CallLLMCard',
  },
  file_content_extraction: {
    type: 'file_content_extraction',
    ...agentDisplay,
    CardComponent: 'FileContentExtractionCard',
  },
  audio_transcription: {
    type: 'audio_transcription',
    ...agentDisplay,
    CardComponent: 'AudioTranscriptionCard',
  },
  condition: {
    type: 'condition',
    ...conditionDisplay,
    CardComponent: 'ConditionCard',
  },
  delay: {
    type: 'delay',
    ...delayDisplay,
    CardComponent: 'DelayCard',
  },
  goal: {
    type: 'goal',
    ...goalDisplay,
  },
  activity_logged_goal: {
    ...goalDisplay,
    type: 'activity_logged_goal',
    CardComponent: 'ActivityLoggedCard',
  },
  scheduled_activity_overdue_goal: {
    ...goalDisplay,
    type: 'scheduled_activity_overdue_goal',
    CardComponent: 'ActivityOverdueCard',
  },
  field_updated_goal: {
    ...goalDisplay,
    type: 'field_updated_goal',
    CardComponent: 'FieldUpdatedCard',
  },
  contact_tag_added_removed_goal: {
    ...goalDisplay,
    type: 'contact_tag_added_removed_goal',
    CardComponent: 'ContactTagAddedRemovedCard',
  },
  on_or_around_date_goal: {
    ...goalDisplay,
    type: 'on_or_around_date_goal',
    CardComponent: 'OnOrAroundDateCard',
  },
  email_received_goal: {
    ...goalDisplay,
    type: 'email_received_goal',
    CardComponent: 'EmailReceivedCard',
  },
  email_link_clicked_goal: {
    ...goalDisplay,
    type: 'email_link_clicked_goal',
    CardComponent: 'EmailLinkClickedCard',
  },
  form_submitted_goal: {
    ...goalDisplay,
    type: 'form_submitted_goal',
    CardComponent: 'FormSubmittedCard',
  },
  stage_updated_goal: {
    ...goalDisplay,
    type: 'stage_updated_goal',
    CardComponent: 'StageUpdatedCard',
  },
  survey_submitted_goal: {
    ...goalDisplay,
    type: 'survey_submitted_goal',
    CardComponent: 'SurveySubmittedCard',
  },
  manual: {
    ...triggerDisplay,
    type: 'manual',
    CardComponent: 'ManualTriggerCard',
  },
  add_trigger: {
    ...triggerDisplay,
    type: 'add_trigger',
    CardComponent: 'AddTriggerCard',
  },
  new_entity_created: {
    type: 'new_entity_created',
    ...triggerDisplay,
    CardComponent: 'NewEntityCreatedCard',
  },
  activity_logged: {
    type: 'activity_logged',
    ...triggerDisplay,
    CardComponent: 'ActivityLoggedCard',
  },
  scheduled_activity_overdue: {
    ...triggerDisplay,
    type: 'scheduled_activity_overdue',
    CardComponent: 'ActivityOverdueCard',
  },
  email_interaction: {
    type: 'email_interaction',
    ...triggerDisplay,
  },
  email_link_clicked: {
    type: 'email_link_clicked',
    ...triggerDisplay,
    CardComponent: 'EmailLinkClickedCard',
  },
  field_updated: {
    type: 'field_updated',
    ...triggerDisplay,
    CardComponent: 'FieldUpdatedCard',
  },
  form_submitted: {
    type: 'form_submitted',
    ...triggerDisplay,
    CardComponent: 'FormSubmittedCard',
  },
  interaction_occurred: {
    type: 'interaction_occurred',
    ...triggerDisplay,
  },
  on_or_around_date: {
    type: 'on_or_around_date',
    ...triggerDisplay,
    CardComponent: 'OnOrAroundDateCard',
  },
  email_received: {
    ...triggerDisplay,
    type: 'email_received',
    CardComponent: 'EmailReceivedCard',
  },
  survey_submitted: {
    type: 'survey_submitted',
    ...triggerDisplay,
    CardComponent: 'SurveySubmittedCard',
  },
  contact_tag_added_removed: {
    type: 'contact_tag_added_removed',
    ...triggerDisplay,
    CardComponent: 'ContactTagAddedRemovedCard',
  },
  website_visited: {
    type: 'website_visited',
    ...triggerDisplay,
  },
  stage_updated: {
    type: 'stage_updated',
    ...triggerDisplay,
    CardComponent: 'StageUpdatedCard',
  },
  schedule: {
    type: 'schedule',
    ...triggerDisplay,
    CardComponent: 'ScheduleCard',
  },
  webhook: {
    type: 'webhook',
    ...triggerDisplay,
    CardComponent: 'WebhookCard',
  },
  unknown: {
    type: 'unknown',
    ...STEP_NODE_CONFIG[STEP_NODE_TYPES.ERROR],
    label: (t: TFunction) => t('Unknown'),
    movable: true,
    creatable: false,
    deletable: true,
    prefix: 'step',
    CardComponent: 'ErrorCard',
  },
  initialize_variable_add: {
    ...variableDefinitionDisplay,
    type: 'initialize_variable_add',
    CardComponent: 'VariableIntializionAddCard',
  },
  initialize_variable: {
    ...variableDefinitionDisplay,
    type: 'initialize_variable',
    CardComponent: 'VariableInitializationCard',
    duplicatable: true,
  },
  merge_branches: {
    ...mergeDisplay,
    type: BranchStepTypes.MERGE,
    label: (t: TFunction) => t('Merge'),
    CardComponent: 'MergeCard',
    duplicatable: false,
  },
  noop: {
    ...mergeDisplay,
    type: 'noop',
    label: (t: TFunction) => t('No Opp'),
    CardComponent: 'BranchCard',
    duplicatable: false,
    deletable: false,
  },
  branch: {
    ...mergeDisplay,
    type: BranchStepTypes.BRANCH,
    label: (t: TFunction) => t('Branch'),
    CardComponent: 'BranchCard',
    duplicatable: false,
    deletable: true,
    creatable: true,
  },
  branch_split: {
    ...mergeDisplay,
    type: BranchStepTypes.BRANCH_SPLIT,
    label: (t: TFunction) => t('Branch Split'),
    CardComponent: 'MergeCard',
    duplicatable: false,
  },
  split_branches: {
    ...mergeDisplay,
    type: BranchStepTypes.SPLIT,
    label: (t: TFunction) => t('Split'),
    CardComponent: 'MergeCard',
    duplicatable: false,
  },
  loop: {
    ...loopDisplay,
    type: LoopStepTypes.LOOP,
    CardComponent: 'LoopCard',
    creatable: true,
  },
  next_or_finish: {
    ...loopDisplay,
    type: LoopStepTypes.LOOP_NEXT_OR_FINISH,
    CardComponent: 'LoopNextCard',
    movable: false,
    creatable: false,
    deletable: false,
    duplicatable: false,
    copyable: false,
    mergeable: false,
  },
};

export const getStepConfig = (type?: KizenNodeType) => {
  const config = (type && AUTOMATION_STEPS[type]) || AUTOMATION_STEPS.unknown;
  return config;
};

export const getErrorStepConfig = (type: KizenNodeType) =>
  AUTOMATION_STEPS[type]
    ? {
        ...AUTOMATION_STEPS[type],
        CardComponent: 'ErrorCard',
      }
    : AUTOMATION_STEPS.unknown;

export const edgeLabels = {
  yes: {
    type: 'yes',
    text: 'Yes',
    goalText: 'met',
    color: 'var(--color-button-primary-hover)',
    detailsKey: 'yesSteps',
    isMerge: true,
  },
  no: {
    type: 'no',
    text: 'No',
    goalText: 'not met',
    color: 'var(--color-button-warning-hover)',
    detailsKey: 'noSteps',
    isMerge: true,
  },
};

export const defaultTrigger = {
  id: `new.${STEP_NODE_TYPES.TRIGGER}`,
  type: AUTOMATION_STEPS.manual.type,
  details: {},
  order: 0,
  prefix: triggerDisplay.prefix,
};

export const defaultVariableDefinition = {
  id: `new.${STEP_NODE_TYPES.INITIALIZE_VARIABLE}`,
  type: AUTOMATION_STEPS.initialize_variable_add.type,
  details: {},
  order: 0,
  prefix: variableDefinitionDisplay.prefix,
};

export const isVariableStep = ({ stepType }: { stepType: KizenNodeType }) =>
  stepType === STEP_NODE_TYPES.INITIALIZE_VARIABLE;

export const isAgentStep = (type: KizenNodeType) =>
  [
    AUTOMATION_STEPS.call_llm.type,
    AUTOMATION_STEPS.file_content_extraction.type,
    AUTOMATION_STEPS.audio_transcription.type,
  ].includes(type);
